import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiURl from "../../controllers/Api";
import imgLocation from "../../controllers/imagePath";

const AdminViewDetails = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const tableScrollRef = useRef(null);

  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const FALLBACK_IMG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
        <rect width="100%" height="100%" fill="#f2f2f2"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
          font-family="Arial" font-size="18" fill="#777">
          No Image
        </text>
      </svg>
    `);

  const orderInfo = useMemo(() => {
    return Array.isArray(orderDetails) && orderDetails.length > 0
      ? orderDetails[0]
      : null;
  }, [orderDetails]);

  const products = useMemo(() => {
    return Array.isArray(orderInfo?.products) ? orderInfo.products : [];
  }, [orderInfo]);

  const statusPill = useMemo(() => {
    const s = String(orderInfo?.status || "").toLowerCase();
    if (s === "completed" || s === "delivered") {
      return "bg-[#D4AF37]/15 text-[#2B0000] border-[#D4AF37]/30";
    }
    if (s === "pending") {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
    if (s === "cancelled") {
      return "bg-red-100 text-red-800 border-red-200";
    }
    if (s === "processing") {
      return "bg-blue-100 text-[#2B0000] border-blue-200";
    }
    return "bg-[#F6EFEA] text-[#2B0000]/85 border-[#2B0000]/10";
  }, [orderInfo?.status]);

  const customerDisplayName = useMemo(() => {
    if (!orderInfo) return "N/A";
    return (
      orderInfo.shipping_full_name ||
      `${orderInfo.first_name || ""} ${orderInfo.last_name || ""}`.trim() ||
      "N/A"
    );
  }, [orderInfo]);

  const customerDisplayPhone = useMemo(() => {
    if (!orderInfo) return "N/A";
    return orderInfo.shipping_phone || orderInfo.phone || "N/A";
  }, [orderInfo]);

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const extractImagePath = (imgPath) => {
    if (!imgPath) return null;

    if (typeof imgPath === "string") {
      try {
        const parsed = JSON.parse(imgPath);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
      } catch (e) {}

      if (imgPath.includes(",")) return imgPath.split(",")[0].trim();
      return imgPath;
    }

    if (Array.isArray(imgPath) && imgPath.length > 0) return imgPath[0];
    return null;
  };

  const getImageUrl = (imgPath) => {
    const extractedPath = extractImagePath(imgPath);
    if (!extractedPath) return FALLBACK_IMG;

    if (
      extractedPath.startsWith("http://") ||
      extractedPath.startsWith("https://") ||
      extractedPath.startsWith("data:")
    ) {
      return extractedPath;
    }

    const cleanPath = extractedPath.replace(/^\/+/, "");
    return `${imgLocation}/${cleanPath}`;
  };

  const resolveCouponDiscount = (info, subtotal) => {
    if (!info || subtotal <= 0) return 0;

    const couponId = Number(info?.coupon_id || 0);
    const hasCouponCode =
      info?.coupon_code && String(info.coupon_code).trim() !== "" && info?.coupon_code !== "N/A";

    if (!couponId && !hasCouponCode) return 0;

    const couponType = String(
      info?.coupon_type || info?.discount_type || ""
    ).toLowerCase();

    const fixedCandidates = [
      info?.coupon_value,
      info?.coupon_amount,
      info?.discount_amount,
      info?.discount_value,
      info?.applied_discount,
      info?.flat_discount,
    ];

    const percentageCandidates = [
      info?.coupon_percentage,
      info?.discount_percentage,
      info?.percentage,
    ];

    const fallbackCandidates = [info?.coupon_discount];

    const firstValid = (arr) => {
      for (const val of arr) {
        const num = Number(val);
        if (!Number.isNaN(num) && num > 0) return num;
      }
      return 0;
    };

    const fixedValue = firstValid(fixedCandidates);
    const percentageValue = firstValid(percentageCandidates);
    const fallbackValue = firstValid(fallbackCandidates);

    let discount = 0;

    if (couponType.includes("percent")) {
      discount = (subtotal * percentageValue) / 100;
    } else if (couponType.includes("fixed") || couponType.includes("flat")) {
      discount = fixedValue || fallbackValue;
    } else {
      discount = fixedValue || (percentageValue ? (subtotal * percentageValue) / 100 : fallbackValue);
    }

    return Math.min(subtotal, Number(discount || 0));
  };

  // Enhanced product data with discount type detection
  const enhancedProducts = useMemo(() => {
    return products.map(item => {
      const isBulkDiscount = item.discount_type === 'bulk';
      const paidPrice = Number(item.price || 0);
      const originalPrice = isBulkDiscount ? Number(item.original_price || paidPrice) : paidPrice;
      const quantity = Number(item.quantity || 0);
      const savings = isBulkDiscount ? (originalPrice - paidPrice) * quantity : 0;
      const savingsPercent = isBulkDiscount && originalPrice > 0 
        ? Math.round(((originalPrice - paidPrice) / originalPrice) * 100) 
        : 0;

      return {
        ...item,
        isBulkDiscount,
        originalPrice,
        paidPrice,
        savings,
        savingsPercent,
        displayPrice: paidPrice,
        displayOriginalPrice: isBulkDiscount ? originalPrice : null,
      };
    });
  }, [products]);

  const hasBulkDiscount = useMemo(() => {
    return enhancedProducts.some(p => p.isBulkDiscount);
  }, [enhancedProducts]);

  const priceBreakdown = useMemo(() => {
    const empty = {
      subtotal: 0,
      discountAmount: 0,
      discountedSubtotal: 0,
      totalCgst: 0,
      totalSgst: 0,
      totalIgst: 0,
      totalTax: 0,
      grandTotalBeforeDiscount: 0,
      grandTotalAfterDiscount: 0,
      roundedTotal: 0,
      roundingDiff: 0,
      itemWiseBreakdown: [],
    };

    if (!orderInfo || !enhancedProducts.length) return empty;

    const safeProducts = enhancedProducts.map((item) => {
      const quantity = Number(item.quantity || 0);
      const price = Number(item.paidPrice || 0);
      const itemSubtotal = price * quantity;

      const cgst = Number(item.cgst || 0);
      const sgst = Number(item.sgst || 0);
      const igst = Number(item.igst || 0);

      const totalTax = cgst + sgst + igst;

      const cgstRate = itemSubtotal > 0 ? cgst / itemSubtotal : 0;
      const sgstRate = itemSubtotal > 0 ? sgst / itemSubtotal : 0;
      const igstRate = itemSubtotal > 0 ? igst / itemSubtotal : 0;

      return {
        ...item,
        quantity,
        price,
        itemSubtotal,
        cgst,
        sgst,
        igst,
        totalTax,
        cgstRate,
        sgstRate,
        igstRate,
      };
    });

    const subtotal = safeProducts.reduce((sum, item) => sum + item.itemSubtotal, 0);
    const discountAmount = resolveCouponDiscount(orderInfo, subtotal);
    const discountedSubtotal = Math.max(0, subtotal - discountAmount);

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    const itemWiseBreakdown = safeProducts.map((item) => {
      const discountShare = subtotal > 0 ? (item.itemSubtotal / subtotal) * discountAmount : 0;
      const taxableAfterDiscount = Math.max(0, item.itemSubtotal - discountShare);

      const recalculatedCgst = taxableAfterDiscount * item.cgstRate;
      const recalculatedSgst = taxableAfterDiscount * item.sgstRate;
      const recalculatedIgst = taxableAfterDiscount * item.igstRate;

      totalCgst += recalculatedCgst;
      totalSgst += recalculatedSgst;
      totalIgst += recalculatedIgst;

      return {
        name: item.name || "Unknown",
        product_id: item.product_id,
        size: item.size || "N/A",
        paidPrice: item.paidPrice,
        quantity: item.quantity,
        unitPrice: item.paidPrice,
        originalPrice: item.originalPrice,
        itemSubtotal: Number(item.itemSubtotal.toFixed(2)),
        discountShare: Number(discountShare.toFixed(2)),
        taxableAfterDiscount: Number(taxableAfterDiscount.toFixed(2)),
        cgstRatePercent: Number((item.cgstRate * 100).toFixed(2)),
        sgstRatePercent: Number((item.sgstRate * 100).toFixed(2)),
        igstRatePercent: Number((item.igstRate * 100).toFixed(2)),
        cgst: Number(recalculatedCgst.toFixed(2)),
        sgst: Number(recalculatedSgst.toFixed(2)),
        igst: Number(recalculatedIgst.toFixed(2)),
        finalLineTotal: Number(
          (taxableAfterDiscount + recalculatedCgst + recalculatedSgst + recalculatedIgst).toFixed(2)
        ),
        img_path: item.img_path,
        isBulkDiscount: item.isBulkDiscount,
        savings: item.savings,
        savingsPercent: item.savingsPercent,
      };
    });

    const totalTax = totalCgst + totalSgst + totalIgst;
    const grandTotalBeforeDiscount = subtotal + safeProducts.reduce((sum, item) => sum + item.totalTax, 0);
    const grandTotalAfterDiscount = discountedSubtotal + totalTax;

    const roundedTotal = Math.round(grandTotalAfterDiscount);
    const roundingDiff = roundedTotal - grandTotalAfterDiscount;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      discountedSubtotal: Number(discountedSubtotal.toFixed(2)),
      totalCgst: Number(totalCgst.toFixed(2)),
      totalSgst: Number(totalSgst.toFixed(2)),
      totalIgst: Number(totalIgst.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      grandTotalBeforeDiscount: Number(grandTotalBeforeDiscount.toFixed(2)),
      grandTotalAfterDiscount: Number(grandTotalAfterDiscount.toFixed(2)),
      roundedTotal: Number(roundedTotal.toFixed(2)),
      roundingDiff: Number(roundingDiff.toFixed(2)),
      itemWiseBreakdown,
    };
  }, [orderInfo, enhancedProducts]);

  const hasCoupon = useMemo(() => {
    return priceBreakdown.discountAmount > 0;
  }, [priceBreakdown.discountAmount]);

  const finalAmount = useMemo(() => {
    if (!orderInfo) return 0;
    return Number(priceBreakdown.roundedTotal || 0);
  }, [orderInfo, priceBreakdown.roundedTotal]);

  const scrollLeft = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: -220, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: 220, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const user_id = localStorage.getItem("user_id");

        if (!user_id) {
          setError("User ID is missing. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${ApiURl}/getOrderDetails.php?order_id=${order_id}&user_id=${user_id}`
        );

        const data = await response.json();

        if (data.success) {
          setOrderDetails(data.data);
        } else {
          throw new Error(data.message || "No data available");
        }
      } catch (err) {
        setError(err.message || "Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [order_id]);

  if (loading) {
    return (
      <div className="font-['Poppins',sans-serif] min-h-screen bg-[#E8D9D5] p-4 flex items-center justify-center">
        <div className="w-full max-w-3xl">
          <div className="bg-[#FBF7F2] rounded-[20px] border border-[#2B0000]/10 p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)]">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-[14px] bg-[#D4AF37]/10 animate-pulse" />
              <div className="flex-1 space-y-3">
                <div className="h-5 w-48 bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-4 w-64 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="mt-8 space-y-4">
              <div className="h-14 rounded-[14px] bg-[#F6EFEA] animate-pulse" />
              <div className="h-14 rounded-[14px] bg-[#F6EFEA] animate-pulse" />
              <div className="h-14 rounded-[14px] bg-[#F6EFEA] animate-pulse" />
            </div>
            <p className="mt-6 text-center text-sm text-[#2B0000]/45">
              Loading order details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-['Poppins',sans-serif] min-h-screen bg-[#E8D9D5] p-4 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="bg-[#FBF7F2] rounded-[20px] border border-[#2B0000]/10 p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)] text-center">
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-[14px] flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#2B0000] mb-2">Oops! Something went wrong</h3>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-[#2B0000] text-white font-semibold rounded-xl"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/admin")}
                className="px-6 py-3 bg-[#FBF7F2] border-2 border-[#2B0000]/10 text-[#2B0000]/75 font-semibold rounded-xl"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!orderInfo) {
    return (
      <div className="font-['Poppins',sans-serif] min-h-screen bg-[#E8D9D5] p-4 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="bg-[#FBF7F2] rounded-[20px] border border-[#2B0000]/10 p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)] text-center">
            <h3 className="text-xl font-bold text-[#2B0000] mb-2">No Order Found</h3>
            <p className="text-sm text-[#2B0000]/60 mb-6">We couldn't find any order data.</p>
            <button
              onClick={() => navigate("/admin")}
              className="px-6 py-3 bg-[#2B0000] text-white font-semibold rounded-xl"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-['Poppins',sans-serif] min-h-screen bg-[#E8D9D5] p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 lg:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl lg:text-4xl font-medium tracking-[-0.02em] text-[#2B0000]">
                Order Details
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#2B0000]/60">
                <span className="font-medium">Order #{orderInfo.order_id}</span>
                <span className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" />
                <span>{formatDate(orderInfo.date)}</span>
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${statusPill}`}>
                  {orderInfo.status}
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate("/admin")}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-[#FBF7F2] border-2 border-[#2B0000]/10 text-[#2B0000]/75 font-semibold rounded-xl shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>

        <div className="bg-[#FBF7F2] rounded-[20px] border border-[#2B0000]/10 shadow-[0_14px_40px_rgba(43,0,0,0.06)] overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-[#2B0000]/10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-[#E8D9D5] rounded-[14px] p-5 border border-[#D4AF37]/30">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <h3 className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">
                      Customer Information
                    </h3>

                    <div className="space-y-2">
                      <div>
                        <p className="text-lg font-bold text-[#2B0000]">{customerDisplayName}</p>
                        <p className="text-xs text-[#2B0000]/60">User ID: {orderInfo.user_id}</p>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-[#2B0000]/75">
                        <svg className="w-4 h-4 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span>{customerDisplayPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-left sm:text-right">
                    <p className="text-xs text-[#2B0000]/45 mb-1">Final Amount</p>
                    <p className="text-2xl lg:text-3xl font-bold text-[#D4AF37]">
                      {formatCurrency(finalAmount)}
                    </p>
                    {hasCoupon && (
                      <p className="text-xs text-[#2B0000]/70 mt-1">
                        Discount applied: {formatCurrency(priceBreakdown.discountAmount)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#FBF7F2] rounded-[14px] p-5 border border-[#D4AF37]/25">
                <h3 className="text-xs font-semibold text-[#2B0000] uppercase tracking-wider mb-3">
                  Shipping Address
                </h3>

                <div className="flex items-start gap-2 text-sm">
                  <svg className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>

                  <div className="text-[#2B0000]/85 leading-relaxed break-words">
                    {orderInfo.shipping_full_name && (
                      <span className="font-medium block mb-1">{orderInfo.shipping_full_name}</span>
                    )}
                    <span className="block">
                      {orderInfo.address_line1}
                      {orderInfo.address_line2 ? `, ${orderInfo.address_line2}` : ""}
                    </span>
                    {orderInfo.landmark && (
                      <span className="block text-[#2B0000]/60">Landmark: {orderInfo.landmark}</span>
                    )}
                    <span className="block">
                      {orderInfo.city ? `${orderInfo.city}, ` : ""}
                      {orderInfo.state || ""}
                      {orderInfo.pincode ? ` - ${orderInfo.pincode}` : ""}
                    </span>
                    <span className="block mt-1">
                      Phone: {orderInfo.shipping_phone || orderInfo.phone || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {hasCoupon && (
            <div className="px-4 sm:px-6 pt-4">
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-[14px] p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2B0000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-5-5A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-[#2B0000]">Coupon Applied</p>
                      <p className="text-xs text-[#2B0000]/60">
                        {orderInfo.coupon_code && orderInfo.coupon_code !== "N/A"
                          ? `Code: ${orderInfo.coupon_code}`
                          : "Discount coupon used"}
                      </p>
                    </div>
                  </div>

                  <div className="text-left md:text-right">
                    <p className="text-xs text-[#2B0000]/45">Discount</p>
                    <p className="text-lg font-bold text-[#2B0000]">
                      -{formatCurrency(priceBreakdown.discountAmount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="px-4 sm:px-6 pt-4">
            <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#FBF7F2] rounded-[14px] p-4 border border-[#D4AF37]/25">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-4 h-4 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#2B0000]">
                  Price Summary
                </h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="bg-[#FBF7F2] rounded-xl p-3">
                  <p className="text-[11px] text-[#2B0000]/45">Subtotal</p>
                  <p className="text-sm font-bold text-[#2B0000]">{formatCurrency(priceBreakdown.subtotal)}</p>
                </div>

                {hasCoupon && (
                  <>
                    <div className="bg-[#FBF7F2] rounded-xl p-3 border-l-4 border-[#D4AF37]">
                      <p className="text-[11px] text-[#2B0000]/45">Discount</p>
                      <p className="text-sm font-bold text-[#2B0000]/70">
                        -{formatCurrency(priceBreakdown.discountAmount)}
                      </p>
                    </div>
                    <div className="bg-[#FBF7F2] rounded-xl p-3">
                      <p className="text-[11px] text-[#2B0000]/45">Total Value</p>
                      <p className="text-sm font-bold text-[#2B0000]">
                        {formatCurrency(priceBreakdown.discountedSubtotal)}
                      </p>
                    </div>
                  </>
                )}

                <div className="bg-[#FBF7F2] rounded-xl p-3 border-l-4 border-[#D4AF37]">
                  <p className="text-[11px] text-[#2B0000]/45">CGST</p>
                  <p className="text-sm font-bold text-[#D4AF37]">{formatCurrency(priceBreakdown.totalCgst)}</p>
                </div>

                <div className="bg-[#FBF7F2] rounded-xl p-3 border-l-4 border-[#D4AF37]">
                  <p className="text-[11px] text-[#2B0000]/45">SGST</p>
                  <p className="text-sm font-bold text-[#2B0000]/70">{formatCurrency(priceBreakdown.totalSgst)}</p>
                </div>

                <div className="bg-[#FBF7F2] rounded-xl p-3 border-l-4 border-[#D4AF37]">
                  <p className="text-[11px] text-[#2B0000]/45">IGST</p>
                  <p className="text-sm font-bold text-[#2B0000]/70">{formatCurrency(priceBreakdown.totalIgst)}</p>
                </div>

                <div className="bg-[#FBF7F2] rounded-xl p-3 border-l-4 border-[#D4AF37]">
                  <p className="text-[11px] text-[#2B0000]/45">Final Amount</p>
                  <p className="text-sm font-bold text-[#D4AF37]">{formatCurrency(finalAmount)}</p>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <h4 className="text-xs font-semibold text-[#2B0000]/75 mb-2">Item-wise Calculation After Discount</h4>
                <table className="min-w-full text-xs bg-[#FBF7F2] rounded-xl overflow-hidden">
                  <thead className="bg-[#F6EFEA]">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-center">Size</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-right">Original</th>
                      )}
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-center">Discount Type</th>
                      )}
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-right">Savings</th>
                      )}
                      <th className="px-3 py-2 text-right">Subtotal</th>
                      <th className="px-3 py-2 text-right">Discount</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-right">CGST</th>
                      <th className="px-3 py-2 text-right">SGST</th>
                      <th className="px-3 py-2 text-right">IGST</th>
                      <th className="px-3 py-2 text-right">Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceBreakdown.itemWiseBreakdown.map((item, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="px-3 py-2 font-medium">{item.name}</td>
                        <td className="px-3 py-2 text-center">{item.size}</td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">
                          {item.isBulkDiscount ? (
                            <div className="flex flex-col items-end leading-tight">
                              <span className="text-[#2B0000]/35 line-through text-xs">
                                {formatCurrency(item.originalPrice)}
                              </span>
                              <span className="text-[#2B0000] font-semibold">
                                {formatCurrency(item.paidPrice)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[#2B0000] font-medium">
                              {formatCurrency(item.paidPrice)}
                            </span>
                          )}
                        </td>
                        
                        {hasBulkDiscount && (
                          <td className="px-3 py-2 text-right text-[#2B0000]/45">
                            {item.isBulkDiscount ? formatCurrency(item.originalPrice) : '-'}
                          </td>
                        )}
                        
                        {hasBulkDiscount && (
                          <td className="px-3 py-2 text-center">
                            {item.isBulkDiscount ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#2B0000] text-white text-[10px] font-medium">
                                Bulk
                              </span>
                            ) : (
                              <span className="text-[#2B0000]/35">-</span>
                            )}
                          </td>
                        )}
                        
                        {hasBulkDiscount && (
                          <td className="px-3 py-2 text-right text-[#2B0000]/70 font-medium">
                            {item.isBulkDiscount ? (
                              <>
                                -{formatCurrency(item.savings)}
                                <span className="text-[10px] ml-1">({item.savingsPercent}%)</span>
                              </>
                            ) : '-'}
                          </td>
                        )}
                        
                        <td className="px-3 py-2 text-right">{formatCurrency(item.itemSubtotal)}</td>
                        <td className="px-3 py-2 text-right text-[#2B0000]/70">-{formatCurrency(item.discountShare)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{formatCurrency(item.taxableAfterDiscount)}</td>
                        <td className="px-3 py-2 text-right text-[#D4AF37]">{formatCurrency(item.cgst)}</td>
                        <td className="px-3 py-2 text-right text-[#2B0000]/70">{formatCurrency(item.sgst)}</td>
                        <td className="px-3 py-2 text-right text-[#2B0000]/70">{formatCurrency(item.igst)}</td>
                        <td className="px-3 py-2 text-right font-bold text-[#D4AF37]">{formatCurrency(item.finalLineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 bg-[#FBF7F2] rounded-xl p-4 border border-[#D4AF37]/30">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <p className="text-[11px] text-[#2B0000]/45">Subtotal</p>
                    <p className="text-sm font-bold text-[#2B0000]">{formatCurrency(priceBreakdown.subtotal)}</p>
                  </div>

                  {hasCoupon && (
                    <div>
                      <p className="text-[11px] text-[#2B0000]/45">Discount</p>
                      <p className="text-sm font-bold text-[#2B0000]/70">
                        -{formatCurrency(priceBreakdown.discountAmount)}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-[11px] text-[#2B0000]/45">Tax Total</p>
                    <p className="text-sm font-bold text-[#D4AF37]">{formatCurrency(priceBreakdown.totalTax)}</p>
                  </div>

                  <div>
                    <p className="text-[11px] text-[#2B0000]/45">Rounding</p>
                    <p className="text-sm font-bold text-[#2B0000]/70">
                      {priceBreakdown.roundingDiff >= 0 ? "+" : ""}
                      {formatCurrency(priceBreakdown.roundingDiff)}
                    </p>
                  </div>

                  <div className="bg-[#D4AF37]/10 rounded-xl p-3">
                    <p className="text-[11px] text-[#2B0000]/60">Final Amount</p>
                    <p className="text-lg font-bold text-[#D4AF37]">{formatCurrency(finalAmount)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base lg:text-lg font-bold text-[#2B0000]">Products Ordered</h3>
                <p className="text-xs text-[#2B0000]/60 mt-1">
                  {enhancedProducts.length} item{enhancedProducts.length === 1 ? "" : "s"}
                </p>
              </div>

              {enhancedProducts.length > 3 && (
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={scrollLeft}
                    className="p-2 bg-[#F6EFEA] rounded-xl hover:bg-[#D4AF37]/15"
                    aria-label="Scroll left"
                  >
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={scrollRight}
                    className="p-2 bg-[#F6EFEA] rounded-xl hover:bg-[#D4AF37]/15"
                    aria-label="Scroll right"
                  >
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="hidden md:block overflow-x-auto rounded-xl border border-[#2B0000]/10 bg-[#FBF7F2]">
              <table className="min-w-full w-full table-auto">
                <thead>
                  <tr className="bg-[#2B0000]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Product</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase">Size</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Price</th>
                    {hasBulkDiscount && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Original</th>
                    )}
                    {hasBulkDiscount && (
                      <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase">Type</th>
                    )}
                    {hasBulkDiscount && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Savings</th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Tax</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase">Final</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2B0000]/10">
                  {priceBreakdown.itemWiseBreakdown.length > 0 ? (
                    priceBreakdown.itemWiseBreakdown.map((item) => {
                      const lineTax = item.cgst + item.sgst + item.igst;

                      return (
                        <tr key={item.product_id} className="hover:bg-[#F6EFEA]/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-xl bg-[#F6EFEA] border border-[#2B0000]/10 p-1 overflow-hidden flex-shrink-0">
                                <img
                                  src={getImageUrl(item.img_path)}
                                  alt={item.name}
                                  className="h-full w-full object-contain"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = FALLBACK_IMG;
                                  }}
                                  loading="lazy"
                                />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-[#2B0000]">{item.name}</div>
                                <div className="text-[10px] text-[#2B0000]/45 mt-0.5">ID: {item.product_id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-[#2B0000]/75">{item.size}</td>
                          <td className="px-4 py-3 text-center text-sm font-semibold text-[#2B0000]">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-sm">
                            {item.isBulkDiscount ? (
                              <div className="flex flex-col items-end leading-tight">
                                <span className="text-[#2B0000]/35 line-through text-xs">
                                  {formatCurrency(item.originalPrice)}
                                </span>
                                <span className="text-[#2B0000] font-semibold">
                                  {formatCurrency(item.paidPrice)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-[#2B0000] font-medium">
                                {formatCurrency(item.paidPrice)}
                              </span>
                            )}
                          </td>
                          
                          {hasBulkDiscount && (
                            <td className="px-4 py-3 text-right text-sm text-[#2B0000]/45">
                              {item.isBulkDiscount ? formatCurrency(item.originalPrice) : '-'}
                            </td>
                          )}
                          
                          {hasBulkDiscount && (
                            <td className="px-4 py-3 text-center">
                              {item.isBulkDiscount ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#2B0000] text-white text-xs font-medium">
                                  Bulk
                                </span>
                              ) : (
                                <span className="text-[#2B0000]/35">-</span>
                              )}
                            </td>
                          )}
                          
                          {hasBulkDiscount && (
                            <td className="px-4 py-3 text-right text-sm text-[#2B0000]/70 font-medium">
                              {item.isBulkDiscount ? (
                                <>
                                  -{formatCurrency(item.savings)}
                                  <span className="text-[10px] ml-1">({item.savingsPercent}%)</span>
                                </>
                              ) : '-'}
                            </td>
                          )}
                          
                          <td className="px-4 py-3 text-right text-sm font-semibold text-[#2B0000]">
                            {formatCurrency(item.taxableAfterDiscount)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-[#D4AF37]">
                            {formatCurrency(lineTax)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-[#D4AF37]">
                            {formatCurrency(item.finalLineTotal)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={hasBulkDiscount ? 10 : 7} className="px-4 py-8 text-center text-sm text-[#2B0000]/45">
                        No products available for this order.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="block md:hidden">
              <div className="flex items-center justify-end gap-1 mb-2 text-xs text-[#D4AF37]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span>Swipe to see all columns →</span>
              </div>

              <div
                ref={tableScrollRef}
                className="overflow-x-auto rounded-xl border border-[#2B0000]/10 bg-[#FBF7F2] scroll-smooth"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <table className="min-w-[1200px] w-full">
                  <thead>
                    <tr className="bg-[#2B0000]">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-white uppercase">Product</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-white uppercase">Size</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-white uppercase">Qty</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Price</th>
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Original</th>
                      )}
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-center text-[10px] font-semibold text-white uppercase">Type</th>
                      )}
                      {hasBulkDiscount && (
                        <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Savings</th>
                      )}
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Taxable</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Tax</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase">Final</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2B0000]/10">
                    {priceBreakdown.itemWiseBreakdown.map((item) => {
                      const lineTax = item.cgst + item.sgst + item.igst;

                      return (
                        <tr key={item.product_id}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-10 w-10 rounded-lg bg-[#F6EFEA] border border-[#2B0000]/10 p-1">
                                <img
                                  src={getImageUrl(item.img_path)}
                                  alt={item.name}
                                  className="h-full w-full object-contain"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = FALLBACK_IMG;
                                  }}
                                />
                              </div>
                              <div className="text-xs font-semibold">{item.name}</div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center text-xs">{item.size}</td>
                          <td className="px-3 py-2 text-center text-xs font-semibold">{item.quantity}</td>
                          <td className="px-3 py-2 text-right text-xs">
                            {item.isBulkDiscount ? (
                              <div className="flex flex-col items-end">
                                <span className="text-[#2B0000] font-medium">{formatCurrency(item.paidPrice)}</span>
                                <span className="text-[#2B0000]/35 line-through text-[9px]">{formatCurrency(item.originalPrice)}</span>
                              </div>
                            ) : (
                              <span className="text-[#2B0000]">{formatCurrency(item.paidPrice)}</span>
                            )}
                          </td>
                          
                          {hasBulkDiscount && (
                            <td className="px-3 py-2 text-right text-xs text-[#2B0000]/45">
                              {item.isBulkDiscount ? formatCurrency(item.originalPrice) : '-'}
                            </td>
                          )}
                          
                          {hasBulkDiscount && (
                            <td className="px-3 py-2 text-center">
                              {item.isBulkDiscount ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#2B0000] text-white text-[8px] font-medium">
                                  Bulk
                                </span>
                              ) : (
                                <span className="text-[#2B0000]/35">-</span>
                              )}
                            </td>
                          )}
                          
                          {hasBulkDiscount && (
                            <td className="px-3 py-2 text-right text-xs text-[#2B0000]/70">
                              {item.isBulkDiscount ? `-${formatCurrency(item.savings)}` : '-'}
                            </td>
                          )}
                          
                          <td className="px-3 py-2 text-right text-xs font-semibold">
                            {formatCurrency(item.taxableAfterDiscount)}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-[#D4AF37]">{formatCurrency(lineTax)}</td>
                          <td className="px-3 py-2 text-right text-xs font-bold text-[#D4AF37]">
                            {formatCurrency(item.finalLineTotal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 sm:hidden">
              <button
                onClick={() => navigate("/admin")}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#2B0000] text-white font-semibold rounded-lg text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminViewDetails;